import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { TABLE_CELL_TYPE, TABLE_HEADER_TYPE } from '@/lib/typography';
import {
  activateRowHref,
  activateRowHrefOnKey,
  isRowInteractiveTarget,
} from '@/utils/rowNavigation';

export interface TableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  numeric?: boolean;
  className?: string;
  hideOnMobile?: boolean;
  /** Sets aria-sort on the header. Omit for columns that cannot be sorted. */
  sortDirection?: 'ascending' | 'descending' | 'none';
}

const ROW_HOVER = 'cursor-pointer hover:bg-ds-sunk';

export function ResponsiveTable<T>({
  columns,
  rows,
  getRowKey,
  getRowHref,
  getRowLabel,
  onRowClick,
  mobileCard,
  empty,
  className,
}: {
  columns: TableColumn<T>[];
  rows: T[];
  getRowKey: (row: T, index: number) => string;
  getRowHref?: (row: T) => string | undefined;
  getRowLabel?: (row: T) => string;
  onRowClick?: (row: T) => void;
  mobileCard?: (row: T) => ReactNode;
  empty?: ReactNode;
  className?: string;
}) {
  const navigate = useNavigate();

  if (rows.length === 0 && empty) return <>{empty}</>;

  const rowNav = (row: T) => {
    const href = getRowHref?.(row);
    if (href) {
      return {
        className: ROW_HOVER,
        tabIndex: 0 as const,
        'aria-label': getRowLabel?.(row),
        onClick: (event: MouseEvent<HTMLElement>) => {
          activateRowHref(event, href, navigate);
        },
        onAuxClick: (event: MouseEvent<HTMLElement>) => {
          if (event.button !== 1) return;
          activateRowHref(event, href, navigate);
        },
        onMouseDown: (event: MouseEvent<HTMLElement>) => {
          if (event.button !== 1) return;
          if (isRowInteractiveTarget(event.target)) return;
          event.preventDefault();
        },
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
          activateRowHrefOnKey(event, href, navigate);
        },
      };
    }
    if (!onRowClick) return undefined;
    return {
      className: ROW_HOVER,
      tabIndex: 0 as const,
      'aria-label': getRowLabel?.(row),
      onClick: (event: MouseEvent<HTMLElement>) => {
        if (isRowInteractiveTarget(event.target)) return;
        onRowClick(row);
      },
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter') return;
        if (isRowInteractiveTarget(event.target)) return;
        event.preventDefault();
        onRowClick(row);
      },
    };
  };

  return (
    <div className={className}>
      <div className="hidden overflow-x-auto nav:block">
        <table className="w-full min-w-0 border-collapse">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.id}
                  aria-sort={col.sortDirection}
                  className={cn(
                    TABLE_HEADER_TYPE,
                    'border-b border-ds-line px-s3 pb-s2 text-left',
                    col.numeric && 'text-right tabular-nums',
                    col.className,
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const nav = rowNav(row);
              return (
                <tr key={getRowKey(row, index)} {...nav}>
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className={cn(
                        TABLE_CELL_TYPE,
                        'border-b border-ds-line px-s3 py-s3 align-middle',
                        col.numeric && 'text-right tabular-nums',
                        col.className,
                      )}
                    >
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-s3 nav:hidden">
        {rows.map((row, index) => {
          const nav = rowNav(row);
          return (
            <div
              key={getRowKey(row, index)}
              className={cn('rounded-card bg-ds-card p-s4 shadow-card', nav?.className)}
              tabIndex={nav?.tabIndex}
              aria-label={nav?.['aria-label']}
              onClick={nav?.onClick}
              onAuxClick={nav?.onAuxClick}
              onMouseDown={nav?.onMouseDown}
              onKeyDown={nav?.onKeyDown}
            >
              {mobileCard
                ? mobileCard(row)
                : columns
                    .filter((col) => !col.hideOnMobile)
                    .map((col) => (
                      <div
                        key={col.id}
                        className="flex items-start justify-between gap-s3 border-b border-ds-line py-s2 last:border-b-0"
                      >
                        <span className={cn(TABLE_HEADER_TYPE)}>{col.header}</span>
                        <span
                          className={cn(
                            TABLE_CELL_TYPE,
                            'text-right',
                            col.numeric && 'tabular-nums',
                          )}
                        >
                          {col.cell(row)}
                        </span>
                      </div>
                    ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
